import styled from 'styled-components';

import { COLORS, MOBILE, SORTABLE_ANIM } from '../../../../theme/tokens';
import { Text } from '../../../../components/Text/Text';
import { Button } from '../../../../components/Button';

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const GroupLabel = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

/* ── The list sections (key takeaways, FAQ, sources) ── */

/**
 * A list section is a CARD, not a run of inputs under a label.
 *
 * The SEO panel holds four of these in a row, and as bare stacked fields the
 * boundary between "the last takeaway" and "the first FAQ question" was a gap of
 * the same size as the gap between two takeaways. Enclosing each list says where
 * it ends without a divider, and gives the add action somewhere to sit that is
 * clearly part of THIS list.
 */
export const Section = styled.section`
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceSoft};
  overflow: hidden;
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid ${COLORS.hairline};
`;

export const SectionTitleText = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.textStrong};
`;

/** How many entries the list holds, so a collapsed-looking section still counts. */
export const SectionCount = styled(Text)`
  flex: none;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.textFaint};
`;

/** `$host` makes it the positioned parent of the generating wash. */
export const SectionBody = styled.div<{ $host?: boolean }>`
  position: ${(p) => (p.$host ? 'relative' : 'static')};
  isolation: ${(p) => (p.$host ? 'isolate' : 'auto')};
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px 14px;
`;

/** Stand-in for a row about to be written, so an empty list has something to wash. */
export const GhostRow = styled.li`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 12px;
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
`;

export const GhostBar = styled.span<{ $wide?: boolean }>`
  display: block;
  height: 10px;
  width: ${(p) => (p.$wide ? '62%' : '38%')};
  border-radius: 99px;
  background: ${COLORS.surfaceMuted};
`;

/** Nothing added yet: says what the list is for rather than showing a bare button. */
export const SectionEmpty = styled(Text)`
  display: block;
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.textFaint};
`;

export const AddRow = styled.div`
  display: flex;
`;

/**
 * The add action, as a real secondary button rather than a text link. It reads as
 * something to press, which is what an author looking for "where do I add one"
 * is scanning for. Shorter than the app-wide 48px control: it sits inside a card
 * among 34px-tall inputs, and the full height would dominate them.
 */
export const AddButton = styled(Button)`
  height: 36px;
  padding: 0 14px;
  font-size: 14px;
  gap: 6px;
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* The lifted row is translated over its neighbours; without this the browser
     may still clip it against a scroll container an ancestor puts it in. */
  position: relative;
`;

/**
 * One entry: the drag handle, the fields, and the remove button.
 *
 * The transform choreography is the reading order's, driven by the same hook
 * (see `hooks/useSortableRows`), so the two lists behave identically — the row
 * under the finger tracks it exactly, its neighbours ease out of the way, and
 * the reorder commits only once the released row has arrived.
 */
export const Row = styled.li<{ $dragged: boolean; $shift: number; $animated: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};

  z-index: ${(p) => (p.$dragged ? 2 : 1)};
  transform: translateY(${(p) => p.$shift}px)
    ${(p) => (p.$dragged ? `scale(${SORTABLE_ANIM.LIFT_SCALE})` : '')};
  box-shadow: ${(p) => (p.$dragged ? `0 14px 30px ${COLORS.pageBg}` : 'none')};

  transition: ${(p) =>
    p.$animated
      ? `transform ${p.$dragged ? SORTABLE_ANIM.SETTLE_MS : SORTABLE_ANIM.SHIFT_MS}ms ${SORTABLE_ANIM.EASE},
         box-shadow ${SORTABLE_ANIM.SETTLE_MS}ms ${SORTABLE_ANIM.EASE}`
      : 'none'};
`;

export const Handle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 30px;
  height: 34px;
  margin-top: 6px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.textFaint};
  cursor: grab;
  /* Claim the gesture from the scroller: without this a vertical drag on a phone
     scrolls the page instead of moving the row. */
  touch-action: none;

  &:hover:not(:disabled),
  &:focus-visible:not(:disabled) {
    background: ${COLORS.surfaceMuted};
    color: ${COLORS.text};
  }

  &:active:not(:disabled) {
    cursor: grabbing;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  ${MOBILE} {
    width: 36px;
  }
`;

/** The row's place in the list, which is the order the public page renders in. */
export const Position = styled(Text)`
  flex: none;
  width: 16px;
  margin-top: 13px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.textFaint};
`;

export const RowFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const PairRow = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
  min-width: 0;

  ${MOBILE} {
    flex-direction: column;
  }
`;

export const RemoveButton = styled.button`
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  margin-top: 6px;
  flex: 0 0 auto;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.icon};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${COLORS.surfaceMuted};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

/* ── Tag input ── */

export const TagBox = styled.div<{ $host?: boolean }>`
  position: ${(p) => (p.$host ? 'relative' : 'static')};
  isolation: ${(p) => (p.$host ? 'isolate' : 'auto')};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
`;

export const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 99px;
  background: ${COLORS.surfaceMuted};
  color: ${COLORS.text};
  font-size: 13px;
`;

export const TagRemove = styled.button`
  display: grid;
  place-items: center;
  width: 16px;
  height: 16px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${COLORS.icon};
  cursor: pointer;
  padding: 0;

  &:hover:not(:disabled) {
    color: ${COLORS.textBright};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

export const TagInputEl = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  background: transparent;
  color: ${COLORS.text};
  font-size: 14px;
  font-family: inherit;
  padding: 4px 0;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

export const TagHint = styled(Text)`
  display: block;
  font-size: 12px;
  color: ${COLORS.textFaint};
`;
